import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import {
  ArrowLeft, FloppyDisk, ListBullets, Calculator, Files, Wrench,
  Warning, Power, Eye, Buildings, ListChecks, UploadSimple, Trash,
  Envelope,
} from "@phosphor-icons/react";
import BaremosTab from "@/components/aseguradora/BaremosTab";
import BaremosReparacionTab from "@/components/aseguradora/BaremosReparacionTab";
import ModulosTab from "@/components/aseguradora/ModulosTab";
import SubencargosTab from "@/components/aseguradora/SubencargosTab";
import RepositorioTab from "@/components/aseguradora/RepositorioTab";
import CitaNotificationTab from "@/components/aseguradora/CitaNotificationTab";
import { getToken } from "@/lib/authToken";
const TABS = [
  { k: "datos", label: "Datos", icon: ListBullets },
  { k: "baremos", label: "Baremos honorarios", icon: Calculator },
  { k: "baremos_reparacion", label: "Baremos reparación", icon: Wrench },
  { k: "modulos", label: "Módulos prexistencias", icon: Buildings },
  { k: "subencargos", label: "Subencargos", icon: ListChecks },
  { k: "cita", label: "Notif. citas", icon: Envelope },
  { k: "documentos", label: "Repositorio", icon: Files },
];

const empty = {
  name: "", code: "", type: "intermediario", active: true,
  nombre_compacto: "", direccion_fiscal: "", cif: "",
  plazos: { contacto_asegurado: "", informe_preliminar: "", informe_final: "" },
  limite_reserva: null,
};

export default function AseguradoraDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can.manageInsurers(user);
  const [aseg, setAseg] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("datos");
  const [enCurso, setEnCurso] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const [r, c] = await Promise.all([
      api.get(`/aseguradoras/${id}`),
      api.get(`/aseguradoras/${id}/expedientes-count`),
    ]);
    setAseg(r.data);
    setEnCurso(c.data.en_curso);
    setForm({
      name: r.data.name || "",
      code: r.data.code || "",
      type: r.data.type || "intermediario",
      active: r.data.active ?? true,
      nombre_compacto: r.data.nombre_compacto || "",
      direccion_fiscal: r.data.direccion_fiscal || "",
      cif: r.data.cif || "",
      plazos: {
        contacto_asegurado: r.data.plazos?.contacto_asegurado || "",
        informe_preliminar: r.data.plazos?.informe_preliminar || "",
        informe_final: r.data.plazos?.informe_final || "",
      },
      limite_reserva: r.data.limite_reserva ?? null,
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put(`/aseguradoras/${id}`, form);
      setSavedAt(new Date());
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const setPlazo = (k, v) => setForm({ ...form, plazos: { ...form.plazos, [k]: v } });

  if (!aseg) return <div className="p-10 text-muted text-sm">Cargando...</div>;

  return (
    <div className="flex flex-col h-screen" data-testid="aseguradora-detail">
      <header className="border-b border-border bg-white flex-shrink-0">
        <div className="h-14 px-6 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button onClick={() => navigate("/admin/aseguradoras")} className="text-muted hover:text-foreground p-1" data-testid="back-button">
              <ArrowLeft className="w-4 h-4" weight="bold" />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted">
                Aseguradora · {aseg.type === "directa" ? "Cliente directo" : "Intermediario"}
              </div>
              <h1 className="font-display font-bold text-base tracking-tight">{aseg.name}</h1>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              form.active
                ? "bg-status-success-bg text-status-success-fg border-status-success-bd"
                : "bg-status-pending-bg text-status-pending-fg border-status-pending-bd"
            }`}>
              <Power className="w-3 h-3" weight="bold" />
              {form.active ? "Activa" : "Inactiva"}
            </span>
            {enCurso > 0 && (
              <span className="text-[11px] text-muted">
                {enCurso} parte{enCurso === 1 ? "" : "s"} en curso
              </span>
            )}
          {!canEdit && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted bg-surface">
              <Eye className="w-3 h-3" weight="bold" /> Solo lectura
            </span>
          )}
          {savedAt && <span className="text-[11px] text-muted">Guardado {savedAt.toLocaleTimeString("es-ES")}</span>}
          </div>
          {canEdit && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-aseguradora"
              className="bg-foreground text-white text-xs px-4 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5 hover:bg-foreground/90 disabled:opacity-50"
            >
              <FloppyDisk className="w-3.5 h-3.5" weight="bold" /> Guardar cambios
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="px-6 flex items-center gap-0">
          {TABS.map((t) => {
            const isActive = tab === t.k;
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`px-4 py-2.5 text-[13px] font-medium border-b-2 inline-flex items-center gap-1.5 transition-colors ${
                  isActive ? "border-foreground text-foreground" : "border-transparent text-muted hover:text-foreground"
                }`}
              >
                <Icon className="w-3.5 h-3.5" weight="bold" />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      {err && (
        <div className="px-6 py-3 bg-status-error-bg border-b border-status-error-bd text-status-error-fg text-sm flex items-start gap-2">
          <Warning className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <div className="text-xs">{err}</div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto bg-surface">
        <fieldset disabled={!canEdit} className={!canEdit ? "[&_input]:bg-surface [&_select]:bg-surface [&_textarea]:bg-surface" : ""}>
        {tab === "datos" && (
          <div className="p-6 max-w-3xl space-y-6">
            {/* Datos de compañía */}
            <section className="bg-white border border-border rounded-md p-5">
              <div className="mb-4">
                <h3 className="font-display font-bold text-sm tracking-tight">Datos de compañía</h3>
                <p className="text-xs text-muted">Información identificativa</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label htmlFor="name">Nombre de compañía</label>
                  <input
                    id="name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="field-name"
                    placeholder="Ocaso, S.A. Compañía de Seguros y Reaseguros"
                  />
                </div>
                <div className="col-span-2">
                  <label htmlFor="nombre_compacto">Nombre compacto (bandeja)</label>
                  <input
                    id="nombre_compacto"
                    value={form.nombre_compacto}
                    onChange={(e) => setField("nombre_compacto", e.target.value)}
                    data-testid="field-nombre_compacto"
                    placeholder="Ocaso"
                  />
                  <div className="text-[10px] text-muted mt-1 normal-case tracking-normal">
                    Cómo se mostrará en la lista de expedientes (más corto que el nombre completo)
                  </div>
                </div>
                <div className="col-span-2">
                  <label htmlFor="direccion_fiscal">Dirección fiscal</label>
                  <input
                    id="direccion_fiscal"
                    value={form.direccion_fiscal}
                    onChange={(e) => setField("direccion_fiscal", e.target.value)}
                    data-testid="field-direccion_fiscal"
                    placeholder="Calle Princesa 23, 28008 Madrid"
                  />
                </div>
                <div>
                  <label htmlFor="cif">CIF</label>
                  <input
                    id="cif"
                    value={form.cif}
                    onChange={(e) => setField("cif", e.target.value.toUpperCase())}
                    data-testid="field-cif"
                    placeholder="A28016608"
                  />
                </div>
                <div>
                  <label htmlFor="code">Código interno</label>
                  <input
                    id="code"
                    value={form.code}
                    onChange={(e) => setField("code", e.target.value.toUpperCase())}
                    data-testid="field-code"
                  />
                </div>
              </div>
            </section>

            {/* Logo de la aseguradora — se sube en formato imagen (PNG/SVG/JPG)
                y se muestra en el monograma circular de la bandeja con
                `object-cover` para encajar perfectamente. */}
            <LogoSection aseg={aseg} canEdit={canEdit} onChange={load} />

            {/* Opciones */}
            <section className="bg-white border border-border rounded-md p-5">
              <div className="mb-4">
                <h3 className="font-display font-bold text-sm tracking-tight">Opciones</h3>
                <p className="text-xs text-muted">Estado de la aseguradora</p>
              </div>

              <label
                htmlFor="active"
                className="flex items-start gap-3 p-3 -m-3 rounded-md hover:bg-surface cursor-pointer normal-case tracking-normal"
              >
                <input
                  id="active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                  data-testid="field-active"
                  className="!w-4 !h-4 !p-0 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold normal-case tracking-normal text-foreground">
                    Aseguradora activa
                  </div>
                  <div className="text-xs text-muted mt-0.5">
                    Si la desactivas, no se podrán crear nuevos partes para ella, pero un administrador
                    puede reactivarla. {enCurso > 0 && (
                      <span className="text-status-warning-fg font-medium">
                        Tiene {enCurso} parte{enCurso === 1 ? "" : "s"} en curso — no podrás desactivarla hasta que terminen.
                      </span>
                    )}
                  </div>
                </div>
              </label>
            </section>

            {/* Plazos */}
            <section className="bg-white border border-border rounded-md p-5">
              <div className="mb-4">
                <h3 className="font-display font-bold text-sm tracking-tight">Plazos</h3>
                <p className="text-xs text-muted">Tiempos de respuesta acordados con la compañía</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="contacto_asegurado">Contacto con asegurado</label>
                  <input
                    id="contacto_asegurado"
                    value={form.plazos.contacto_asegurado}
                    onChange={(e) => setPlazo("contacto_asegurado", e.target.value)}
                    data-testid="field-plazo_contacto"
                    placeholder="ej. 24h"
                  />
                </div>
                <div>
                  <label htmlFor="informe_preliminar">Informe preliminar</label>
                  <input
                    id="informe_preliminar"
                    value={form.plazos.informe_preliminar}
                    onChange={(e) => setPlazo("informe_preliminar", e.target.value)}
                    data-testid="field-plazo_preliminar"
                    placeholder="ej. 5 días"
                  />
                </div>
                <div>
                  <label htmlFor="informe_final">Informe final</label>
                  <input
                    id="informe_final"
                    value={form.plazos.informe_final}
                    onChange={(e) => setPlazo("informe_final", e.target.value)}
                    data-testid="field-plazo_final"
                    placeholder="ej. 30 días"
                  />
                </div>
              </div>
            </section>

            {/* Reglas de control */}
            <section className="bg-white border border-border rounded-md p-5">
              <div className="mb-4">
                <h3 className="font-display font-bold text-sm tracking-tight">Reglas de control</h3>
                <p className="text-xs text-muted">Avisos automáticos a Gestor Senior</p>
              </div>
              <div>
                <label htmlFor="limite_reserva">Límite de reserva</label>
                <div className="flex items-center gap-2">
                  <input
                    id="limite_reserva"
                    type="number"
                    step="100"
                    min="0"
                    value={form.limite_reserva ?? ""}
                    onChange={(e) => setField("limite_reserva", e.target.value === "" ? null : parseFloat(e.target.value))}
                    data-testid="field-limite_reserva"
                    placeholder="ej. 6000"
                    className="!font-mono max-w-[180px]"
                  />
                  <span className="text-xs text-muted">€</span>
                </div>
                <div className="text-[10px] text-muted mt-1 normal-case tracking-normal">
                  Si el importe de reserva del expediente supera este límite, se avisará automáticamente a un Gestor Senior.
                  Déjalo vacío para no aplicar regla.
                </div>
              </div>
            </section>
          </div>
        )}

        {tab === "baremos" && <BaremosTab aseguradoraId={id} canEdit={canEdit} />}
        {tab === "baremos_reparacion" && <BaremosReparacionTab aseguradoraId={id} canEdit={canEdit} />}
        {tab === "modulos" && <ModulosTab aseguradoraId={id} canEdit={canEdit} token={getToken()} />}
        {tab === "subencargos" && <SubencargosTab aseguradoraId={id} canEdit={canEdit} />}
        {tab === "cita" && <CitaNotificationTab aseguradoraId={id} canEdit={canEdit} />}
        {tab === "documentos" && <RepositorioTab aseguradoraId={id} aseguradoraName={form?.name || form?.nombre_compacto} canEdit={canEdit} />}
        </fieldset>
      </div>
    </div>
  );
}

function ComingSoon({ title, hint }) {
  return (
    <div className="p-10 max-w-2xl">
      <div className="bg-white border border-dashed border-border rounded-lg p-10 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted mb-2">Próximamente</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-3">{title}</h2>
        <p className="text-sm text-muted leading-relaxed">{hint}</p>
      </div>
    </div>
  );
}


/* ---------- LogoSection ----------
 * Carga el logo de la aseguradora. Lo previsualiza dentro de un círculo
 * 44px (idéntico al de la bandeja) para que el admin compruebe cómo
 * encaja antes de guardar. Sube como base64 al endpoint
 * POST /aseguradoras/:id/logo. */
function LogoSection({ aseg, canEdit, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 1_048_576) {
      setErr("Logo demasiado grande (máx 1 MB).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/aseguradoras/${aseg.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error subiendo el logo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("¿Quitar el logo personalizado? Volverá al SVG por defecto.")) return;
    try {
      await api.delete(`/aseguradoras/${aseg.id}/logo`);
      onChange?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error eliminando el logo");
    }
  };

  if (!aseg) return null;

  return (
    <section className="bg-white border border-border rounded-md p-5" data-testid="logo-section">
      <div className="mb-4">
        <h3 className="font-display font-bold text-sm tracking-tight">Logo</h3>
        <p className="text-xs text-muted">
          Imagen que se muestra en el círculo de la bandeja. Recomendado: PNG/SVG cuadrado, fondo transparente o color de marca. Máx 1 MB.
        </p>
      </div>
      <div className="flex items-center gap-5">
        {/* Preview a tamaño bandeja (44px) */}
        <div className="flex flex-col items-center gap-1.5">
          <div
            className="w-11 h-11 rounded-full overflow-hidden border border-border bg-white flex items-center justify-center"
            data-testid="logo-preview-bandeja"
          >
            {aseg.logo_url ? (
              <img src={aseg.logo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[14px] font-bold text-muted">
                {(aseg.nombre_compacto || aseg.name || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="text-[9.5px] uppercase tracking-wider text-muted font-semibold">Bandeja</span>
        </div>

        {/* Preview a tamaño grande (96px) */}
        <div className="flex flex-col items-center gap-1.5">
          <div
            className="w-24 h-24 rounded-2xl overflow-hidden border border-border bg-white flex items-center justify-center"
            data-testid="logo-preview-grande"
          >
            {aseg.logo_url ? (
              <img src={aseg.logo_url} alt="" className="w-full h-full object-contain" />
            ) : (
              <span className="text-3xl font-bold text-muted">
                {(aseg.nombre_compacto || aseg.name || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="text-[9.5px] uppercase tracking-wider text-muted font-semibold">Detalle</span>
        </div>

        <div className="flex-1 space-y-2">
          {canEdit && (
            <>
              <label
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-[12.5px] font-semibold cursor-pointer transition-colors ${
                  uploading
                    ? "bg-brand-soft text-brand"
                    : "bg-foreground text-white hover:bg-foreground/90"
                }`}
                data-testid="logo-upload-label"
              >
                <UploadSimple className="w-4 h-4" weight="bold" />
                {uploading ? "Subiendo…" : aseg.logo_url ? "Reemplazar logo" : "Subir logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="logo-file-input"
                />
              </label>

              {aseg.logo_url && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="ml-2 inline-flex items-center gap-1 px-2.5 h-9 rounded-md text-[12px] font-medium text-muted border border-border bg-white hover:text-status-error-fg hover:border-status-error-bd transition-colors"
                  data-testid="logo-delete-btn"
                >
                  <Trash className="w-3.5 h-3.5" weight="regular" />
                  Quitar
                </button>
              )}
            </>
          )}
          {err && (
            <div className="text-[11.5px] text-status-error-fg inline-flex items-center gap-1">
              <Warning className="w-3 h-3" weight="bold" />
              {err}
            </div>
          )}
          {!aseg.logo_url && (
            <p className="text-[11px] text-muted leading-snug">
              Sin logo personalizado. Se usará el logo por defecto si está en la lista de aseguradoras conocidas, o la inicial del nombre.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
